// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, AppIcon, Button, Spinner, Section } from '@loa/ui-components';
import { authApi } from '../api/authApi';
import './RegistrationPage.css';

interface TokenValidation {
  valid: boolean;
  message?: string;
  advisorId?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  brokerageName?: string;
  temporaryPassword?: string;
  keycloakLoginUrl?: string;
}

export function RegistrationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [validation, setValidation] = useState<TokenValidation | null>(null);

  useEffect(() => {
    if (!token) {
      setValidation({ valid: false, message: 'No registration token provided' });
      setLoading(false);
      return;
    }

    const validateToken = async () => {
      try {
        const response = await authApi.validateRegistrationToken(token);
        setValidation(response);

        // Store token in sessionStorage for RegistrationSuccessPage to complete registration
        // This is needed because Keycloak redirects to /registration-success without the token
        sessionStorage.setItem('registration_token', token);

        setLoading(false);
      } catch (error) {
        console.error('Token validation failed:', error);
        setValidation({
          valid: false,
          message: 'Failed to validate registration token. Please try again.',
        });
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  if (loading) {
    return (
      <div className='registration-container'>
        <Section padded className='registration-segment'>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spinner />
          </div>
        </Section>
      </div>
    );
  }

  if (!token || (validation && !validation.valid)) {
    return (
      <div className='registration-container'>
        <Section padded className='registration-segment'>
          <h2 style={{ textAlign: 'center' }}>
            <AppIcon name="warning sign" color="red" />
            Invalid Registration Link
          </h2>
          <Alert variant="error" header="Registration Error">
            <p>{validation?.message || 'This registration link is invalid or has expired.'}</p>
          </Alert>
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <Button variant="filled" onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </div>
        </Section>
      </div>
    );
  }

  return (
    <div className='registration-container'>
      <Section padded className='registration-segment'>
        <h2 style={{ textAlign: 'center' }}>
          <AppIcon name="key" />
          Complete Your Registration
        </h2>

        <h3 style={{ textAlign: 'center', color: '#666' }}>
          {validation?.brokerageName}
        </h3>

        <Alert variant="info" header={`Welcome, ${validation?.firstName}!`}>
          <p>
            Please login below with your email (<strong>{validation?.email}</strong>) and the temporary
            password that was sent to your email.
          </p>
          <p>
            After logging in, you will be prompted to create a new password for your account.
          </p>
        </Alert>

        {validation?.temporaryPassword && (
          <Alert variant="warning" header="Temporary Password (from your email)">
            <p style={{ fontSize: '1.2em', fontFamily: 'monospace', fontWeight: 'bold' }}>
              {validation.temporaryPassword}
            </p>
          </Alert>
        )}

        <div style={{ padding: 0, border: 'none', boxShadow: 'none' }}>
          <iframe
            src={validation?.keycloakLoginUrl}
            style={{
              width: '100%',
              height: '500px',
              border: '1px solid #ddd',
              borderRadius: '4px',
            }}
            title='Login'
          />
        </div>

        <Alert variant="info" header="Next Steps:">
          <ol style={{ lineHeight: '1.8' }}>
            <li>Enter your email and the temporary password shown above</li>
            <li>Click "Sign In"</li>
            <li>You will be prompted to create a new password</li>
            <li>After setting your password, you'll be redirected to your dashboard</li>
          </ol>
        </Alert>
      </Section>
    </div>
  );
}
